import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import {
  SEVERITY_LABEL,
  Badge,
  Button,
  EmptyState,
  ScoreBadge,
  Spinner,
} from '@/components/ui/primitives';
import { api } from '@/lib/api';
import { cn, formatRelative } from '@/lib/utils';

type Tab = 'findings' | 'attributes' | 'jsonld';

export function ProductDrawer({
  tenantId,
  productId,
  onClose,
}: {
  tenantId: string;
  productId: number;
  onClose(): void;
}) {
  const [tab, setTab] = useState<Tab>('findings');

  const detail = useQuery({
    queryKey: ['product', tenantId, productId],
    queryFn: () => api.catalog.product(tenantId, productId),
  });

  const jsonLd = useQuery({
    queryKey: ['jsonld', tenantId, productId],
    queryFn: () => api.catalog.jsonLd(tenantId, productId),
    enabled: tab === 'jsonld',
  });

  const title =
    detail.data?.snapshot.product.name ??
    detail.data?.snapshot.product.title ??
    `Produto #${productId}`;

  return (
    <Dialog.Root
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/70" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed inset-y-0 right-0 z-50 flex h-full w-full max-w-2xl flex-col overflow-hidden border-l border-[var(--color-hairline)] bg-[var(--color-surface-soft)] outline-none"
        >
          <header className="flex items-start justify-between gap-3 border-b border-[var(--color-hairline)] px-5 py-4">
            <div className="min-w-0">
              <Dialog.Title className="truncate text-sm font-semibold text-[var(--color-ink)]">
                {title}
              </Dialog.Title>
              <p className="text-xs text-[var(--color-muted)]">
                #{productId}
                {detail.data ? ` · ingerido ${formatRelative(detail.data.fetchedAt)}` : ''}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {detail.data ? <ScoreBadge score={detail.data.score} /> : null}
              <Dialog.Close asChild>
                <Button variant="ghost" size="sm" aria-label="Fechar">
                  <X className="size-4" />
                </Button>
              </Dialog.Close>
            </div>
          </header>

          <nav className="flex gap-1 border-b border-[var(--color-hairline)] px-3 py-2">
            {(
              [
                ['findings', 'Problemas'],
                ['attributes', 'Atributos'],
                ['jsonld', 'JSON-LD'],
              ] as Array<[Tab, string]>
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setTab(value)}
                className={cn(
                  'rounded-[10px] px-3 py-1.5 text-xs font-medium transition',
                  tab === value
                    ? 'bg-[var(--color-surface-card)] text-[var(--color-ink)]'
                    : 'text-[var(--color-muted)] hover:bg-[var(--color-surface-elevated)]',
                )}
              >
                {label}
              </button>
            ))}
          </nav>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-5">
            {detail.isLoading ? (
              <Spinner />
            ) : !detail.data ? (
              <EmptyState title="Produto não encontrado no snapshot" />
            ) : tab === 'findings' ? (
              detail.data.findings.length === 0 ? (
                <EmptyState title="Nenhum problema detectado" />
              ) : (
                <ul className="space-y-2">
                  {detail.data.findings.map((finding) => (
                    <li
                      key={finding.id}
                      className="rounded-[10px] border border-[var(--color-hairline)] bg-[var(--color-surface-card)] px-3 py-2"
                    >
                      <div className="flex items-center gap-2">
                        <Badge tone={finding.severity as 'critical'}>
                          {SEVERITY_LABEL[finding.severity] ?? finding.severity}
                        </Badge>
                        <span className="text-xs font-medium text-[var(--color-ink)]">
                          {finding.ruleId}
                        </span>
                        {finding.field ? (
                          <span className="text-[11px] text-[var(--color-muted)]">{finding.field}</span>
                        ) : null}
                      </div>
                      <p className="mt-1 text-xs text-[var(--color-body)]">{finding.message}</p>
                    </li>
                  ))}
                </ul>
              )
            ) : tab === 'attributes' ? (
              <AttributesTab detail={detail.data} />
            ) : jsonLd.isLoading ? (
              <Spinner />
            ) : (
              <pre className="overflow-x-auto rounded-[10px] border border-[var(--color-hairline)] bg-[var(--color-surface-card)] p-3 font-mono text-[11px] leading-relaxed text-[var(--color-body)]">
                {JSON.stringify(jsonLd.data ?? {}, null, 2)}
              </pre>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function AttributesTab({
  detail,
}: {
  detail: NonNullable<Awaited<ReturnType<typeof api.catalog.product>>>;
}) {
  const { product, specifications, skus } = detail.snapshot;
  return (
    <div className="space-y-4 text-xs">
      <Section title="Produto">
        <Field label="Nome (catálogo)" value={product.name} />
        <Field label="Title (SEO)" value={product.title} />
        <Field label="Meta description" value={product.metaTagDescription} />
        <Field label="Palavras-chave" value={product.keywords} />
        <Field label="Marca" value={product.brandName} />
        <Field label="Categoria" value={detail.snapshot.categoryPath.join(' > ')} />
        <Field label="Descrição" value={product.description} multiline />
      </Section>

      <Section title={`Especificações (${specifications.length})`}>
        {specifications.length === 0 ? (
          <p className="text-[var(--color-muted)]">Nenhuma especificação preenchida.</p>
        ) : (
          specifications.map((spec) => (
            <Field key={spec.name} label={spec.name} value={spec.values.join(', ')} />
          ))
        )}
      </Section>

      <Section title={`SKUs (${skus.length})`}>
        {skus.map((sku) => (
          <div
            key={sku.id}
            className="rounded-[10px] border border-[var(--color-hairline)] bg-[var(--color-surface-card)] px-3 py-2"
          >
            <p className="font-medium text-[var(--color-ink)]">
              <span className="text-[var(--color-muted)]">Nome do SKU · </span>
              {sku.name} <span className="text-[var(--color-muted)]">#{sku.id}</span>
            </p>
            <p className="text-[var(--color-muted)]">
              EAN {sku.ean || '—'} · {sku.images.length} imagens ·{' '}
              {sku.images.filter((image) => !image.text?.trim()).length} sem alt text
            </p>
          </div>
        ))}
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-[1.5px] text-[var(--color-muted)]">
        {title}
      </p>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function Field({
  label,
  value,
  multiline,
}: {
  label: string;
  value: string | null | undefined;
  multiline?: boolean;
}) {
  return (
    <div className={multiline ? '' : 'flex gap-2'}>
      <span className="w-36 shrink-0 text-[var(--color-muted)]">{label}</span>
      <span
        className={cn(
          'min-w-0 flex-1 text-[var(--color-body)]',
          multiline && 'mt-1 block whitespace-pre-wrap',
        )}
      >
        {value?.trim() ? value : <em className="text-[var(--color-muted)]">vazio</em>}
      </span>
    </div>
  );
}
